<template>
    <v-form>
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>
            <v-card-text>
                <v-container grid-list-md>
                    <v-layout wrap>
                        <v-flex xs12 sm6>
                            <v-text-field
                                v-model="value.containerNumber"
                                label="What's the container number?"
                                hint="(Sticker or Laser)"
                                placeholder="e.g. 005123"
                                v-validate="'required|isInteger|number-is-unique:' + value.id"
                                :error-messages="errors.first('container number')"
                                name="container number"
                                :maxlength="6"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-text-field
                                v-model="value.alternateNumber"
                                label="What's the alternate number?"
                                placeholder="e.g. SK601"
                                name="alternate number"
                                maxlength="12"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-autocomplete
                                v-model="value.containerConfiguration"
                                label="What's the container type?"
                                name="container type"
                                :items="containerConfigurations"
                                item-value="id"
                                item-text="name"
                                v-validate="'required'"
                                :error-messages="errors.first('container type')"
                                return-object
                                autocomplete="off"
                                @change="filterContainers(value.containerConfiguration)"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-autocomplete
                                v-model="value.containerSize"
                                label="What's the container size?"
                                name="container size"
                                :items="containerSizesForConfiguration"
                                item-value="id"
                                item-text="description"
                                v-validate="'required'"
                                :error-messages="errors.first('container size')"
                                return-object
                                autocomplete="off"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12>
                            <v-autocomplete
                                v-model="value.department"
                                label="What's the department?"
                                name="department"
                                :items="departments"
                                item-value="id"
                                item-text="codeAndName"
                                v-validate="'required'"
                                :error-messages="errors.first('department')"
                                return-object
                                autocomplete="off"
                                @change="reValidate('department', value.department)"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12>
                            <v-autocomplete
                                v-model="value.garageLocation"
                                label="What's the garage location?"
                                name="container garage"
                                :items="garageLocations"
                                item-value="id"
                                item-text="location"
                                v-validate="'required'"
                                :error-messages="errors.first('container garage')"
                                return-object
                                autocomplete="off"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12>
                            <v-text-field
                                v-model="value.description"
                                label="Any additional description?"
                                placeholder="e.g. galvanised; painted"
                                :error-messages="errors.first('description')"
                                name="description"
                                maxlength="1024"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12 mb-n6>
                            <v-menu
                                v-model="firstDayOfServiceDialog"
                                :close-on-content-click="false"
                                max-width="290"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        :value="formattedFirstDayOfService"
                                        label="What's the container manufacture date?"
                                        readonly
                                        v-validate="'required'"
                                        :error-messages="errors.first('manufacture date')"
                                        name="manufacture date"
                                        v-on="on"

                                        append-icon="event"
                                        @click:append="firstDayOfServiceDialog = true"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="value.firstDayOfService"
                                    @change="firstDayOfServiceDialog = false; reValidate('manufacture date', formattedFirstDayOfService)"
                                    type="month"
                                    :max="this.today"
                                    no-title
                                ></v-date-picker>
                            </v-menu>
                        </v-flex>
                        <v-flex xs12 mt-n4 mb-n2>
                            <v-checkbox
                                v-model="value.isDecommissioned"
                            >
                                <template v-slot:label>This container is decommissioned</template>
                            </v-checkbox>
                        </v-flex>
                        <v-flex xs12 mt-n2 mb-n3 v-if="value.isDecommissioned">
                            <v-select
                                label="What's the reason for decommissioning?"
                                item-value="key"
                                item-text="value"
                                :items="reasons"
                                v-model="value.decommissionReason"
                                v-validate="'required'"
                                :error-messages="errors.collect('reason')"
                                name="reason"
                                required
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 v-if="value.isDecommissioned">
                            <v-text-field
                                v-model="value.decommissionReasonOther"
                                label="What's the reason details?"
                                placeholder=""
                                v-validate="otherReasonRequired ? 'required' : ''"
                                :error-messages="errors.first('other reason')"
                                name="other reason"
                                maxlength="250"
                                required
                            ></v-text-field>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <slot name="actions"></slot>
                <v-btn
                    color="primary"
                    @click="submit"
                >{{ !this.editing ? 'Save Changes' : 'Update' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

  import Configuration from '@/components/Configurations/ConfigurationsClient.vue';
  import {ConfigurationType, DecommissionReason} from '@/models/enums';
  import Container from '@/components/Containers/ContainersClient.vue';
  import FormValidation from '@/components/FormValidation.vue';
  import moment from 'moment';
  import {DateFormat} from '@/plugins/date-filter';
  import Date from '@/components/Date.vue';
  import GarageLocations from '@/components/GarageLocations/GarageLocationsClient';
  import {Validator} from 'vee-validate';

  export default {
    name: 'ContainerForm',
    props: {
      value: {
        required: true
      },
      filterGarages: {
        required: true
      }
    },
    components: {
      Date,
    },
    mixins: [FormValidation, Configuration, Container, GarageLocations],
    computed: {
      today() {
        return moment().format(DateFormat.Canonical);
      },
      formTitle() {
        return !this.editing ? 'Add Container' : 'Edit Container';
      },
      editing() {
        return !!this.value.id;
      },
      formattedFirstDayOfService() {
        return this.value.firstDayOfService ? moment(this.value.firstDayOfService).format(DateFormat.MonthDisplay) : '';
      },
      containerConfigurations() {
        return this.configurations.filter(c => c.type === ConfigurationType.Container);
      },
      otherReasonRequired() {
        return this.value.decommissionReason === DecommissionReason.Other;
      }
    },
    methods: {
      async submitForm() {
        if (this.value.firstDayOfService && this.value.firstDayOfService.length === 7) {
          this.value.firstDayOfService = this.value.firstDayOfService + '-01';
        }
        this.value.containerConfigurationId = this.value.containerConfiguration && this.value.containerConfiguration.id;
        this.value.containerSizeId = this.value.containerSize && this.value.containerSize.id;
        this.value.garageLocationId = this.value.garageLocation && this.value.garageLocation.id;
        this.value.departmentId = this.value.department && this.value.department.id;

        if (!this.editing) {
          this.$emit('add', {id: this.id, model: this.value});
        } else {
          this.$emit('edit', {id: this.value.id, model: this.value});
        }
      },
      filterContainers(configuration) {
        if (!configuration) {
          return [];
        }
        let filteredSizes = this.containerSizes.filter(x => configuration.containerSizes.find(v => v === x.id));
        this.containerSizesForConfiguration = filteredSizes;

        const existingSizeId = this.value.containerSizeId || this.value.containerSize && this.value.containerSize.Id;
        if (existingSizeId) {
          this.value.containerSize = filteredSizes.find(x => x.id === existingSizeId);
        }
      },
    },
    data() {
      return {
        id: '',
        firstDayOfServiceDialog: false,
        rules: {
          required: value => !!value || 'Required',
          containerNumber: value => {
            const pattern = /^([0-9]{1,6})$/;
            return pattern.test(value) || 'The sort order field must be a 6 digit number';
          }
        },
        configurations: [],
        containerSizes: [],
        containerSizesForConfiguration: [],
        containerSize: { },
        garageLocations: [],
        departments: [],
        reasons: [
          {
            key: DecommissionReason.Scrapped,
            value: 'Scrapped'
          },
          {
            key: DecommissionReason.Sold,
            value: 'Sold'
          },
          {
            key: DecommissionReason.Missing,
            value: 'Missing'
          },
          {
            key: DecommissionReason.Other,
            value: 'Other'
          }
        ]
      };
    },
    watch: {
      async value(val) {
        if (val.id) {
          this.id = val.id;
          this.value.garageLocation = this.garageLocations && this.garageLocations.find(x => x.id === this.value.garageLocationId);
          this.value.department = this.departments && this.departments.find(x => x.id === this.value.departmentId);
          this.value.containerConfiguration = this.configurations && this.configurations.find(x => x.id === this.value.containerConfigurationId);
          this.filterContainers(this.value.containerConfiguration);
        } else {
          this.id = this.$uuid.v1();
          this.filterContainers(this.value.containerConfiguration);
        }
      },
      garageLocations: {
        handler() {
          this.value.garageLocation = this.garageLocations.find(x => x.id === this.value.garageLocationId);
        },
        deep: true
      },
      departments: {
        handler() {
          this.value.department = this.departments.find(x => x.id === this.value.departmentId);
        },
        deep: true
      },
      configurations: {
        handler() {
          this.value.containerConfiguration = this.configurations.find(x => x.id === this.value.containerConfigurationId);
        },
        deep: true
      },
    },
    async created() {
      const isUniqueNumber = async (value, params) => {
        return await this.checkNumberIsUnique(params[0], value);
      };
      const isInteger = (value) => {
        const pattern = /^([0-9]{1,6})$/;
        return {
          valid : pattern.test(value),
          data: {
            message: 'The sort order field must be a 6 digit number'
          }
        };
      };

      Validator.extend('number-is-unique', {
        validate: isUniqueNumber,
        getMessage: (field, params, data) => data.message
      });
      Validator.extend('isInteger', {
        validate: isInteger,
        getMessage: (field, params, data) => data.message
      });
    },

    async mounted() {
      this.configurations = await this.getConfigurations();
      this.containerSizes = await this.getContainerSizes();

      const garages = await this.getContainerGarageLocations();
      if (this.userIsWorkshopManager) {
        this.garageLocations = garages.filter(r => this.filterGarages.find(v => v === r.id));
      } else {
        this.garageLocations = garages;
      }

      this.departments = await this.getDepartments();

      if (this.value.containerSizeId) {
        this.filterContainers(this.value.containerConfiguration);
      }
    },
  };
</script>
